<template>
  <Toast />
  <ConfirmDialog group="headless">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="flex flex-col items-center p-8 bg-surface-0 dark:bg-surface-900 rounded">
        <div
          class="rounded-full bg-white outline outline-black inline-flex justify-center items-center h-24 w-24 -mt-20">
          <i class="pi pi-question text-5xl"></i>
        </div>
        <span class="font-bold text-2xl block mb-2 mt-6">{{ message.header }}</span>
        <p class="mb-0">{{ message.message }}</p>
        <div class="flex items-center gap-2 mt-6">
          <Button :label="message.rejectLabel" outlined @click="rejectCallback" class="w-32"></Button>
          <Button :label="message.acceptLabel" @click="acceptCallback" class="w-32"></Button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
  <ModalUnit v-if="modalShowUnit" @closeModal="closeModal" :unit="selectedUnit" />
  <IndexView />
  <div id="overlay" class="fixed inset-0 bg-black bg-opacity-30 z-30" :class="{ hidden: !isBlur }"></div>
  <ViewContainer>
    <section class="p-6">
      <PageTitle title="Unit" />
      <div class="bg-white dark:bg-dark-900 w-full rounded-lg py-6 px-5 shadow-lg">
        <div id="table-header" class="mb-5 flex justify-between">
          <AddButton to_url="#" button_text="+ Tambah Unit" button_class="text-base py-2 font-medium"
            @click="showModal" />
        </div>
        <div class="overflow-x-auto">
          <div class="inline-block min-w-full align-middle">
            <div class="overflow-hidden shadow-lg rounded-lg">
              <TableComponent>
                <TableHead>
                  <TableRow>
                    <TableHeadCell>No</TableHeadCell>
                    <TableHeadCell>Code</TableHeadCell>
                    <TableHeadCell>Nama Unit</TableHeadCell>
                    <TableHeadCell>Action</TableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow v-if="allUnits.length == 0">
                    <TableCell colspan="5" customClass="text-center italic text-gray-400 dark:text-gray-500"
                      noTextColor>
                      Belum ada data
                    </TableCell>
                  </TableRow>
                  <TableRow v-else v-for="(unit, index) in allUnits" :key="unit.id" :index="index" isBody>
                    <TableCell>{{ index + 1 }}</TableCell>
                    <TableCell>{{ unit.code }}</TableCell>
                    <TableCell>{{ unit.name }}</TableCell>
                    <TableCell noTextColor customClass="text-gray-500 dark:text-gray-400 flex">
                      <button :id="'edit-data-' + unit.id" @click="editUnit(unit)"
                        class="border border-gray-400 hover:border-green-700 hover:bg-green-400 flex justify-center items-center mx-1 p-2 rounded-md group">
                        <PencilSquareIcon class="h-4 w-4 group-hover:text-green-700" />
                      </button>
                      <button :id="'delete-data-' + unit.id" @click="deleteUnit(unit)"
                        class="border border-gray-400 hover:border-red-500 hover:bg-red-400 flex justify-center items-center mx-1 p-2 rounded-md group">
                        <TrashIcon class="h-4 w-4 group-hover:text-red-700" />
                      </button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </TableComponent>
              <PaginationButtons v-model="currentPage" :totalPages="totalPages" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </ViewContainer>
</template>

<script>
import IndexView from "@/views/IndexView.vue";
import PageTitle from "@/components/PageTitle.vue";
import AddButton from "@/components/buttons/AddButton.vue";
import ModalUnit from "@/components/modals/ModalUnit.vue";
import ViewContainer from "@/components/ViewContainer.vue";
import {
  TableComponent,
  TableHead,
  TableRow,
  TableHeadCell,
  TableBody,
  TableCell,
} from "@/components/table";
import { PencilSquareIcon, TrashIcon } from "@heroicons/vue/24/solid";
import { mapGetters, mapActions } from "vuex";
import PaginationButtons from "@/components/PaginationButtons.vue";
import { Button, ConfirmDialog, Toast } from "primevue";

export default {
  name: "UnitView",
  components: {
    IndexView, PageTitle, AddButton, ModalUnit, ViewContainer, TableComponent, TableHead, TableRow, TableHeadCell, TableBody, TableCell, PencilSquareIcon, TrashIcon, PaginationButtons, ConfirmDialog, Button, Toast
  },
  data() {
    return {
      modalShowUnit: false,
      isBlur: false,
      selectedUnit: null,
      currentPage: 1,
      totalPages: 1,
    };
  },
  computed: {
    ...mapGetters("units", ["allUnits"]),
  },
  created() {
    this.fetchUnits();
  },
  watch: {
    currentPage: {
      immediate: true,
      handler(newVal) {
        this.fetchUnits({ page: newVal });
      },
    }
  },
  methods: {
    ...mapActions("units", ["deleteUnitById"]),
    async fetchUnits(params = {}) {
      if (!params.page) params.page = this.currentPage;

      try {
        const { pagination } = await this.$store.dispatch("units/fetchUnits", params);
        if (pagination) {
          this.totalPages = pagination.totalPages;
        }
      } catch (error) {
        console.log(error);
      }
    },
    showModal() {
      this.modalShowUnit = true;
      this.isBlur = true;
      this.selectedUnit = null;
    },
    closeModal() {
      setTimeout(() => {
        this.modalShowUnit = false;
        this.isBlur = false;
        this.fetchUnits();
      }, 200);
    },
    editUnit(unit) {
      this.selectedUnit = unit;
      this.modalShowUnit = true;
      this.isBlur = true;
    },
    async deleteUnit(unit) {
      this.$confirm.require({
        group: 'headless',
        header: 'Apakah anda yakin ingin menghapus unit?',
        message: 'Silahkan konfirmasi sebelum diproses.',
        acceptLabel: 'Ya',
        rejectLabel: 'Tidak',
        accept: async () => {
          try {
            await this.deleteUnitById(unit.id);
            this.fetchUnits();
            this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Berhasil menghapus unit', life: 3000 });
          }
          catch (error) {
            console.error('Gagal menghapus unit', error);
            this.$toast.add({ severity: 'error', summary: 'Failed', detail: 'Gagal menghapus unit', life: 3000 });
          }
        },
        reject: () => {
          this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'Batal menghapus unit', life: 3000 });
        }
      });
    },
  },
};
</script>